<script lang="ts" setup>
import { useQuery } from '@urql/vue';
import { PageContainer } from '~ds/components/ui/page-container';
import { Heading } from '~ds/components/ui/typography';

import type { FaqSection } from '~/graphql/generated/graphql';

import { fetchPublicLearningFaqs } from '~/graphql/documents/campaigns';

interface Props {
  section: FaqSection;
  title: string;
}

const props = defineProps<Props>();

// Not useFaqFetch: guests on the schools clusters pass a selection allowlist
// that rejects the shared fragment document, so this queries the fragment-free
// fetchPublicLearningFaqs the gate admits.
const { data, fetching: loadingFaqs } = useQuery({
  query: fetchPublicLearningFaqs,
  variables: computed(() => ({
    first: 50,
    sections: [props.section],
    orderBy: [{ column: 'ORDER' as const, order: 'ASC' as const }],
  })),
});

const dynamicFaqs = computed(() =>
  (data.value?.learningFaqs?.data ?? []).map(faq => ({
    heading: faq.question,
    content: typeof faq.answer === 'string' ? faq.answer : '',
  })),
);

const analytics = useAnalyticsService();
const handleFaqClick = (faqHeading: string) => {
  analytics.track(EVENTS.SCHOOLS_FAQ_OPENED, {
    section: props.section,
    faqHeading: slugify(faqHeading),
  });
};
</script>

<template>
  <!-- The FAQ set is content-managed, so a section with no rows yet renders nothing rather
       than an empty heading. -->
  <PageContainer
    v-if="!loadingFaqs && dynamicFaqs.length"
    as="section"
    size="xl"
    data-testid="schools-landing-faq"
  >
    <KUICoreFaqSection
      class="mx-auto max-w-4xl"
      is-rich-text
      :faqs="dynamicFaqs"
      heading-align="center"
      @faq-opened="handleFaqClick"
    >
      <template #title>
        <Heading
          as="h2"
          size="3xl"
          class="mb-12"
          data-testid="schools-landing-faq-title"
        >
          {{ title }}
        </Heading>
      </template>
    </KUICoreFaqSection>
  </PageContainer>
</template>
